import { Box, Slider, Stack } from "@mui/material";
import React, { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import AvatarEditor, { Position } from "react-avatar-editor";
import { useTranslation } from "react-i18next";

/**
 * An external interface to be able to manually access data from a parent component.  To use this 
 * functionality, in the parent component create a `useRef` that is assigned to this component. 
 * Then use these functions to access the desired data.  For example, `myRef.current.getImage()`.
 */
export interface GetImageHandler {
  /**
   * Gets the image with current edits.
   */
  getImage(): HTMLCanvasElement | null;
  /**
   * Gets the image with current edits as a base64 encoded string.  This format is useful for 
   * persisting the image content.
   */
  getImageAsBase64EncodedString(): string;
}

interface EditAvatarImageProps {
  /**
   * A file representing the avatar image and must be of the image/* MIME type.
   */
  file: File,
  /**
   * Callback fired when the image center point position changes.
   * @param value The new center point position.
   */
  onPositionChange: (value: Position) => void;
  /**
   * Callback fired when the image zoom changes.
   * @param value The new image zoom.
   */
  onZoomChange: (value: number) => void;
  /**
   * The image center point position.
   */
  position: Position;
  /**
   * Scale the image down to the editor size.
   * @default true
   */
  scaleImage?: boolean;
  /**
   * The image zoom.
   */
  zoom: number;
  /**
   * The maximum amount the image can be scaled (zoomed-in).
   * @default 8
   */
  zoomFactor?: number;
  /**
   * The number of steps to go from no zoom to fully zoomed in.  A higher number of steps increases 
   * zoom precision and smooths the transition, but at a cost of more steps to go through by mouse 
   * scroll or scrollbar dragging.
   * @default 100
   */
  zoomSteps?: number;
}

const EditAvatarImage = forwardRef(function EditAvatarImage(props: EditAvatarImageProps, ref: ForwardedRef<GetImageHandler>): React.JSX.Element {
  const { i18n } = useTranslation();
  const editorRef = useRef<AvatarEditor>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  const [zoom, setZoom] = useState(0);
  const [position, setPosition] = useState<Position>({x: 0.5, y: 0.5});

  const zoomFactor = props.zoomFactor ?? 8;
  const zoomSteps = props.zoomSteps ?? 100;
  const scaleImage = props.scaleImage ?? true;
  
  if (props.position.x !== position.x || props.position.y !== position.y) {
    setPosition(props.position);
  }
  if (props.zoom !== zoom) {
    setZoom(props.zoom);
  }

  useImperativeHandle(ref, (): GetImageHandler => {
    return {
      getImage,
      getImageAsBase64EncodedString
    };
  }, []);

  useEffect(() => {
    boxRef.current?.addEventListener("wheel", handleScroll);
    return () => {
      boxRef.current?.removeEventListener("wheel", handleScroll);
    }
  }, [boxRef.current])

  function handleScroll(event: WheelEvent): void {
    let clampedValue = 0;
    setZoom((current) => {
      const delta = event.deltaY * zoomFactor/zoomSteps * -1;
      const newValue = current + delta;
      // Make sure the zoom is clamped between 0 and the max `zoomFactor` to keep the image in 
      // bounds.
      clampedValue = Math.min(Math.max(newValue, 0), zoomSteps);
      return clampedValue;
    });
    // Wait for next frame to fire the event to make sure the setState finishes first.
    setTimeout(() => {
      props.onZoomChange(clampedValue);
    })
  }

  function handlePositionChange(position: Position): void {
    setPosition(position);
    props.onPositionChange(position);
  }

  function handleZoomChange(event: Event, value: number | number[]): void {
    if (typeof value === "number") {
      setZoom(value);
    }
  };

  function calculateZoom(currentStep: number): number {
    // Perform a smooth zoom for any image size based on a dynamic number of steps using a 
    // geometric progression of zooms.
    let currentZoomFactor = Math.pow(zoomFactor, currentStep/zoomSteps);
    return currentZoomFactor;
  }

  function getImage(): HTMLCanvasElement | null {
    if (!editorRef.current) {
      return null;
    }

    return scaleImage ? editorRef.current.getImageScaledToCanvas()
                      : editorRef.current.getImage();
  }

  function getImageAsBase64EncodedString(): string {
    const image = getImage();
    return image ? image.toDataURL() : "";
  }

  return (
    <Box ref={boxRef}>
      <Stack direction="row" spacing={1} sx={{
        justifyContent: "center"
      }}>
        <AvatarEditor 
          ref={editorRef}
          border={0}
          borderRadius={250}
          color={[0, 0, 0, 0.65]}
          height={250}
          image={props.file}
          onPositionChange={handlePositionChange}
          position={position}
          scale={calculateZoom(zoom)}
          width={250}
        />
        <Stack spacing={1} sx={{
          alignItems: "center"
        }}>
          <Slider 
            max={zoomSteps}
            min={0}
            onChange={handleZoomChange}
            orientation="vertical"
            sx={{flex: 1}}
            value={zoom}
            valueLabelDisplay="off"
          />
          <Box>{i18n.t("edit-avatar-image.zoom-button")}</Box>
        </Stack>
      </Stack>
    </Box>
  );
});

export default EditAvatarImage;
